import Heading from 'components/Heading';
import React from 'react';
import { SimpleShowLayout, useRecordContext, useResourceContext } from 'react-admin';
import { RateSettingsDisplayHelper } from 'utils/displayHelpers/rate-settings';
import { RateSettingsFields } from './RateSettingsFields';
import { RateSettingsHeading } from './RateSettingsHeading';
import { isFieldVisible } from '../../utils/fieldsVisibility';
import { RATE_TYPE_RESTRICTED_RESOURCES } from '../constants/tax-classification';
import { Stack } from '@mui/material';

interface SettingsProps {
  source: string;
  showTitle?: boolean;
}

interface DefaultSettingsProps extends SettingsProps {
  taxClassification: 'ten99' | 'w2.local' | 'w2.travel';
}

const DefaultTaxClassificationComponent = ({
  source,
  taxClassification,
  showTitle = true,
}: DefaultSettingsProps) => {
  const record = useRecordContext();
  const resource = useResourceContext();
  const { location } = record;
  const CoreShowLayout = showTitle ? SimpleShowLayout : React.Fragment;

  const rateSettingsDisplayHelper = new RateSettingsDisplayHelper({
    location,
    settings: record[source],
  });
  const activeRate = rateSettingsDisplayHelper.getActiveRate(taxClassification);
  const isRateTypeRestrictedResource = RATE_TYPE_RESTRICTED_RESOURCES.includes(resource);

  function getTitle() {
    const titles = {
      ten99: '1099',
      ['w2.local']: 'W2 Local',
      ['w2.travel']: 'W2 Travel',
    };
    return titles[taxClassification];
  }

  function shouldShowRateSettings(rateType: '8-hour-rate' | 'blended-rate') {
    const levelShouldShowSettingsConfig = isFieldVisible('blended_non_blended_settings', resource);

    if (!levelShouldShowSettingsConfig) {
      return false;
    }

    if (isRateTypeRestrictedResource) {
      return activeRate === rateType;
    }

    return true;
  }

  return (
    <CoreShowLayout>
      {showTitle && <Heading>{getTitle()}</Heading>}
      <RateSettingsHeading source={source} taxClassification={taxClassification} />
      <Stack sx={{ pb: 2, pl: 2 }}>
        {shouldShowRateSettings('8-hour-rate') ? (
          <RateSettingsFields
            source={source}
            type="8-hour-rate"
            taxClassification={taxClassification}
            activeRate={activeRate}
          />
        ) : null}
        {shouldShowRateSettings('blended-rate') ? (
          <RateSettingsFields
            source={source}
            type="blended-rate"
            taxClassification={taxClassification}
            activeRate={activeRate}
          />
        ) : null}
      </Stack>
    </CoreShowLayout>
  );
};

export const Ten99Section = ({ source, showTitle = true }: SettingsProps) => {
  return (
    <DefaultTaxClassificationComponent
      source={source}
      taxClassification="ten99"
      showTitle={showTitle}
    />
  );
};

export const W2LocalSection = ({ source, showTitle = true }: SettingsProps) => {
  return (
    <DefaultTaxClassificationComponent
      source={source}
      taxClassification="w2.local"
      showTitle={showTitle}
    />
  );
};

export const W2TravelSection = ({ source, showTitle = true }: SettingsProps) => {
  return (
    <DefaultTaxClassificationComponent
      source={source}
      taxClassification="w2.travel"
      showTitle={showTitle}
    />
  );
};
